
import { defineComponent, toRefs, computed } from "vue";
import VueApexCharts from "vue3-apexcharts";
import moment from "moment";
import { RevenueBreakdownData } from "@/domain/entities/RevenueDashboard";
import { useI18n } from 'vue-i18n';
import { AccountController } from '@/app/ui/controllers/AccountController';
import formatPriceRP from '@/app/infrastructures/misc/common-library/v2/FormatPriceRP';

interface Series {
  name: string;
  data: number[];
}

interface SeriesChart {
  originalSeries: Series;
  compareSeries: Series;
}

interface SeriesData {
  omzetSeries: SeriesChart;
  sttSeries: SeriesChart;
  weightSeries: SeriesChart;
}

interface TotalRevenue {
  originalRevenue: number[];
  compareRevenue: number[];
}

interface Categories {
  originalDate: string[];
  compareDate: string[];
}

interface ChartData {
  seriesData: SeriesData;
  categories: Categories;
  dataRevenue: TotalRevenue;
}

export default defineComponent({
  name: "LineComparisonChart",
  props: {
    data: {
      type: Object as () => RevenueBreakdownData,
      required: true
    },
    type: {
      type: String,
      default: "omzet"
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    hasError: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ""
    },
    height: {
      type: Number,
      default: 350
    }
  },
  components: {
    VueApexCharts
  },
  setup(props) {
    // PROPS
    const { t } = useI18n();
    const { data, type, height } = toRefs(props);

    function getSeries(type: string) {
      const chartData: any = data.value;
      const fieldMap = {
        omzet: "totalProfit",
        stt: "totalStt",
        berat: "totalWeightKg",
        revenue: "totalRevenue"
      } as any;

      const field = fieldMap[type] ?? "";

      const chartMap =
        chartData.chart?.map((item: any) => [item.date, item[field]]) || [];

      const compMap =
        chartData.comparisonChart?.map((item: any) => [
          item.date,
          item[field]
        ]) || [];

      const currentSeries = chartMap?.map((item: any) => item[1]);
      const comparisonSeries = compMap?.map((item: any) => item[1]);

      return [
        { name: t("dashboard.lineComparisonChart.current"), data: currentSeries },
        { name: t("dashboard.lineComparisonChart.comparison"), data: comparisonSeries }
      ];
    }

    // UTILS
    const monthNames = [
      t("dashboard.lineComparisonChart.monthsShort.jan"),
      t("dashboard.lineComparisonChart.monthsShort.feb"),
      t("dashboard.lineComparisonChart.monthsShort.mar"),
      t("dashboard.lineComparisonChart.monthsShort.apr"),
      t("dashboard.lineComparisonChart.monthsShort.may"),
      t("dashboard.lineComparisonChart.monthsShort.jun"),
      t("dashboard.lineComparisonChart.monthsShort.jul"),
      t("dashboard.lineComparisonChart.monthsShort.aug"),
      t("dashboard.lineComparisonChart.monthsShort.sep"),
      t("dashboard.lineComparisonChart.monthsShort.oct"),
      t("dashboard.lineComparisonChart.monthsShort.nov"),
      t("dashboard.lineComparisonChart.monthsShort.dec")
    ];
    const formatDateMonth = (date: string): string => {
      if (!date) return "";

      const rangeMatch = date.match(
        /^(\d{4}-\d{2}-\d{2})\s*-\s*(\d{4}-\d{2}-\d{2})(?:\s*\((Week|Month)\s*(\d+)\))?$/
      );

      if (rangeMatch) {
        const [, start, end, labelType, labelNumber] = rangeMatch;

        const s = moment(start);
        const e = moment(end);

        const sDay = s.date();
        const eDay = e.date();
        const month = monthNames[e.month()];

        const label =
          labelType && labelNumber ? ` (${labelType} ${labelNumber})` : "";

        return `${sDay} - ${eDay} ${month}${label}`;
      }

      // Daily date fallback
      const parsed = moment(date);
      if (!parsed.isValid()) return date;

      return `${parsed.date()} ${monthNames[parsed.month()]}`;
    };
    const formatValue = (value: number) => {
      if (value >= 1000000) {
        return (value / 1000000).toFixed(1).replace(/\.0$/, "") + "jt";
      } else if (value >= 1000) {
        return (value / 1000).toFixed(0) + "rb";
      }
      return value.toFixed(0);
    };
    const suffixYaxis = (value: number) => {
      return type.value === "stt"
        ? `${value.toFixed(0)} STT`
        : type.value === "berat"
        ? `${value.toFixed(1)} kg`
        : formatValue(value);
    };
    const formatDateRange = (dateArray: string[]): string => {
      if (!dateArray.length) return "";

      const allDates: moment.Moment[] = [];

      dateArray.forEach((d: any) => {
        if (!d) return;

        // Check if it's a range with Week/Month
        const rangeMatch = d.match(
          /(\d{4}-\d{2}-\d{2})\s*-\s*(\d{4}-\d{2}-\d{2})/
        );
        if (rangeMatch) {
          const start = moment(rangeMatch[1]);
          const end = moment(rangeMatch[2]);
          allDates.push(start, end);
        } else {
          // Single day
          allDates.push(moment(d));
        }
      });

      if (!allDates.length) return "";

      // Sort dates
      const parsed = allDates.sort((a, b) => a.valueOf() - b.valueOf());

      const start = parsed[0] || "";
      const end = parsed[parsed.length - 1] || "";

      // Single day
      if (start.isSame(end, "day")) {
        return formatDateMonth(start.format());
      }

      // Same month & year
      if (start.month() === end.month() && start.year() === end.year()) {
        return `${start.date()} - ${end.date()} ${
          monthNames[end.month()]
        } ${end.year()}`;
      }

      // Same year
      if (start.year() === end.year()) {
        return `${formatDateMonth(start.format())} - ${formatDateMonth(
          end.format()
        )} ${end.year()}`;
      }

      // Different years
      return `${formatDateMonth(
        start.format()
      )} ${start.year()} - ${formatDateMonth(end.format())} ${end.year()}`;
    };

    function formatWeeklyMonthlyLabel(
      originDates: string,
      compareDates: string
    ): string {
      const formatRange = (dateStr: string) => {
        if (!dateStr) return "";

        const clean = dateStr.replace(/\((Week|Month) \d+\)/i, "").trim();
        const [startStr, endStr] = clean.split(" - ").map((s) => s.trim());
        if (!startStr) return "";

        const start = moment(startStr, "YYYY-MM-DD");
        const end = endStr ? moment(endStr, "YYYY-MM-DD") : start;

        return start.month() === end.month()
          ? `${start.date()} - ${end.date()} ${monthNames[start.month()]}`
          : `${start.date()} ${monthNames[start.month()]} - ${end.date()} ${
              monthNames[end.month()]
            }`;
      };

      const originFormatted = formatRange(originDates);
      const compareFormatted = compareDates ? formatRange(compareDates) : "";

      const originEndStr = originDates
        .split(" - ")
        .pop()
        ?.replace(/\((Week|Month) \d+\)/i, "")
        .trim();
      const year = originEndStr
        ? moment(originEndStr, "YYYY-MM-DD").year()
        : "";

      return compareFormatted
        ? `${originFormatted} vs ${compareFormatted} ${year}`
        : `${originFormatted} ${year}`;
    }

    // CHART DATA
    const dateLists = computed(() => {
      const chartData: any = data.value;

      const chartMap = chartData.chart?.map((item: any) => item.date) || [];
      const compMap =
        chartData.comparisonChart?.map((item: any) => item.date) || [];

      // Map date => formatted string
      const originalDate = chartMap.map((item: any) => (item ? item : ""));
      const compareDate = compMap.map((item: any) => (item ? item : ""));
      return { originalDate, compareDate };
    });

    const mergedDates = computed(() => {
      const originalDates = dateLists.value.originalDate;
      const compareDates = dateLists.value.compareDate;
      return originalDates.map((item: any, idx: number) => {
        const compare = formatDateMonth(compareDates[idx]);
        const original = formatDateMonth(item);
        if (item && compare) return `${original} | ${compare}`;
        return original || compare || "";
      });
    });
    const title = computed(() => {
      return `${t("dashboard.lineComparisonChart.performance")} ${type.value === "stt" ? "STT" : t(`dashboard.lineComparisonChart.${type.value}`)}`;
    });

    const accountCurrency = computed(() => {
      return (
        (AccountController.accountData?.accountCurrency as "IDR" | "MYR") ??
        "IDR"
      );
    });
    const chartOptions = computed(() => {
      return {
        chart: {
          fontFamily: "Montserrat",
          height: height.value,
          offsetY: 10,
          toolbar: {
            show: true,
            offsetY: 20,
            tools: {
              download: false,
              pan: true,
              zoom: false,
              zoomin: true,
              zoomout: true,
              reset: true
            }
          },
          zoom: {
            enabled: true,
            type: "x",
            autoScaleYaxis: true
          }
        },
        title: {
          text: title.value,
          align: "left",
          offsetX: 10,
          offsetY: 10,
          style: {
            fontSize: "16px",
            fontWeight: 600,
            fontFamily: "Montserrat",
            color: "#333333"
          }
        },
        stroke: {
          width: 3,
          colors: ["#36B37E", "#5BC1F4"],
          curve: "smooth",
          fill: {
            type: "solid",
            opacity: 0.85
          }
        },
        xaxis: {
          categories: mergedDates.value,
          rangePadding: "padding",
          labels: {
            rotate: -65,
            useHTML: true,
            style: {
              fontFamily: "Montserrat",
              fontWeight: 400,
              fontSize: "8px",
              cssClass: "apexcharts-xaxis-label"
            },
            hideOverlappingLabels: true,
            formatter: function(val: string) {
              if (!val || typeof val !== "string") return "";

              const [originRaw, compareRaw] = val
                .split("|")
                .map((v) => v.trim());

              const safeSplit = (text: string) => {
                if (!text) return { datePart: "", labelPart: "" };
                const m = text.match(/^(.*?)(\s\((?:Week|Month) \d+\))?$/i);
                return {
                  datePart: m ? m[1].trim() : "",
                  labelPart: m && m[2] ? m[2].trim() : ""
                };
              };

              const o = safeSplit(originRaw);
              const c = safeSplit(compareRaw);

              // DAILY (single-line)
              const isDaily =
                !originRaw.toLowerCase().includes("week") &&
                !originRaw.toLowerCase().includes("month") &&
                !originRaw.includes("(") &&
                !originRaw.includes("-");

              if (isDaily) {
                return val;
              }

              // WEEK / MONTH → Multi-line formatting with \n
              if (c.datePart) {
                return [
                  `${o.datePart} | ${c.datePart}`,
                  `${o.labelPart}   ${c.labelPart}`
                ];
              }

              // Only origin weekly/monthly
              return `${o.datePart}\n${o.labelPart}`;
            }
          },
          tickAmount: Math.min(mergedDates.value.length, 10),
          tooltip: {
            enabled: false
          }
        },
        yaxis: {
          labels: {
            show: true,
            formatter: (value: number) => suffixYaxis(value),
            style: {
              fontFamily: "Montserrat",
              fontWeight: 400,
              fontSize: "10px",
              cssClass: "apexcharts-yaxis-label"
            },
            offsetX: 0,
            offsetY: 0,
            rotate: 0
          }
        },
        grid: {
          show: true,
          borderColor: "#8BD3B5",
          strokeDashArray: 5,
          position: "back",
          xaxis: {
            lines: {
              show: true
            }
          },
          yaxis: {
            lines: {
              show: false
            }
          }
        },
        states: {
          hover: {
            filter: {
              type: "none"
            }
          }
        },
        dataLabels: {
          enabled: false
        },
        markers: {
          size: 6,
          colors: ["#36B37E", "#5BC1F4"]
        },
        fill: {
          type: "gradient",
          gradient: {
            opacityFrom: 0.4,
            opacityTo: 0.2
          },
          colors: ["#36B37E", "#5BC1F4"]
        },
        legend: {
          show:
            dateLists.value.originalDate.filter(Boolean).length > 0 ||
            dateLists.value.compareDate.filter(Boolean).length > 0,
          onItemClick: { toggleDataSeries: false },
          onItemHover: { highlightDataSeries: false },
          position: "top",
          horizontalAlign: "right",
          fontFamily: "Montserrat",
          fontSize: "10px",
          fontWeight: 500,
          labels: {
            colors: "#4D4D4D"
          },
          markers: {
            width: 10,
            height: 10,
            radius: 12, // round markers
            fillColors: ["#36B37E", "#5BC1F4"] // matches your line colors
          },
          itemMargin: {
            horizontal: 10,
            vertical: 4
          },
          formatter: (seriesName: string, opts: any) => {
            const seriesIndex = opts.seriesIndex;
            return (
              formatDateRange(
                seriesIndex === 1
                  ? dateLists.value.compareDate.filter(Boolean)
                  : dateLists.value.originalDate.filter(Boolean)
              ) || "-"
            );
          }
        },
        tooltip: {
          shared: false,
          intersect: true,
          custom: ({
            series,
            seriesIndex,
            dataPointIndex,
            w
          }: {
            series: number[][];
            seriesIndex: number;
            dataPointIndex: number;
            w: any;
          }) => {
            const listCategory = mergedDates.value.map((item: any) =>
              item.replace("|", "vs")
            );
            const isDaily =
              !listCategory[dataPointIndex].toLowerCase().includes("week") &&
              !listCategory[dataPointIndex].toLowerCase().includes("month");
            const category = isDaily
              ? listCategory[dataPointIndex]
              : formatWeeklyMonthlyLabel(
                  dateLists.value.originalDate[dataPointIndex] || "",
                  dateLists.value.compareDate[dataPointIndex] || ""
                );
            const omzetA = getSeries("omzet")[0].data[dataPointIndex];
            const omzetB = getSeries("omzet")[1].data[dataPointIndex];
            const sttA = getSeries("stt")[0].data[dataPointIndex];
            const sttB = getSeries("stt")[1].data[dataPointIndex];
            const weightA = getSeries("berat")[0].data[dataPointIndex];
            const weightB = getSeries("berat")[1].data[dataPointIndex];
            const revenueA = getSeries("revenue")[0].data[dataPointIndex];
            const revenueB = getSeries("revenue")[1].data[dataPointIndex];
            return `
              <div style="border-radius:6px;box-shadow:0 2px 6px rgba(0,0,0,0.2);font-size:12px;">
                <div style="background:#36B37E;padding:6px 8px;color:#fff;">
                  ${category}
                </div>
                <div style="display:flex;flex-direction:column;background:#fff;padding:6px 8px;gap:8px;">
                  ${
                    omzetA || omzetB
                      ? `<div style="display:flex;align-items:center;gap:8px;">
                    <span style="color:#A7A8AA;">${t("dashboard.lineComparisonChart.totalOmzet")}</span>
                    ${
                      omzetA
                        ? `<div style="display:flex;align-items:center;gap:4px;">
                      <div style="width:5px;height:5px;background:#36B37E;border-radius: 9999px;"></div>
                      <span style="color:#4D4D4D;">${formatPriceRP(
                        omzetA,
                        accountCurrency.value
                      )}</span>
                    </div>`
                        : ""
                    }
                    ${
                      omzetB
                        ? `<div style="display:flex;align-items:center;gap:4px;">
                      <div style="width:5px;height:5px;background:#58BFF3;border-radius: 9999px;"></div>
                      <span style="color:#4D4D4D;">${formatPriceRP(
                        omzetB,
                        accountCurrency.value
                      )}</span>
                    </div>`
                        : ""
                    }
                  </div>`
                      : ""
                  }
                  ${
                    revenueA || revenueB
                      ? `<div style="display:flex;align-items:center;gap:8px;">
                    <span style="color:#A7A8AA;">${t("dashboard.lineComparisonChart.totalRevenue")}</span>
                    ${
                      revenueA
                        ? `<div style="display:flex;align-items:center;gap:4px;">
                      <div style="width:5px;height:5px;background:#36B37E;border-radius: 9999px;"></div>
                      <span style="color:#4D4D4D;">${formatPriceRP(
                        revenueA,
                        accountCurrency.value
                      )}</span>
                    </div>`
                        : ""
                    }
                    ${
                      revenueB
                        ? `<div style="display:flex;align-items:center;gap:4px;">
                      <div style="width:5px;height:5px;background:#58BFF3;border-radius: 9999px;"></div>
                      <span style="color:#4D4D4D;">${formatPriceRP(
                        revenueB,
                        accountCurrency.value
                      )}</span>
                    </div>`
                        : ""
                    }
                  </div>`
                      : ""
                  }
                  ${
                    sttA || sttB
                      ? `<div style="display:flex;align-items:center;gap:8px;">
                    <span style="color:#A7A8AA;">${t("dashboard.lineComparisonChart.totalStt")}</span>
                    ${
                      sttA
                        ? `<div style="display:flex;align-items:center;gap:4px;">
                      <div style="width:5px;height:5px;background:#36B37E;border-radius: 9999px;"></div>
                      <span style="color:#4D4D4D;">${sttA} STT (${weightA}KG)</span>
                    </div>`
                        : ""
                    }
                    ${
                      sttB
                        ? `<div style="display:flex;align-items:center;gap:4px;">
                      <div style="width:5px;height:5px;background:#58BFF3;border-radius: 9999px;"></div>
                      <span style="color:#4D4D4D;">${sttB} STT (${weightB}KG)</span>
                    </div>`
                        : ""
                    }
                  </div>`
                      : ""
                  }
                </div>
              </div>
            `;
          }
        }
      };
    });

    const series = computed(() => getSeries(type.value));

    return {
      chartOptions,
      series,
      title
    };
  }
});
